import { Table, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaEdit, FaClock } from "react-icons/fa";
import {
  HourglassSplit,
  SendFill,
  CalendarX,
  CheckCircleFill,
  XCircleFill,
  ExclamationCircle,
  PenFill
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { formatDateTimeBR, normalizeText } from "../../../utils";
import { PATHS } from "../../../routes/paths";
import type { ComprovantesSindicatoRow } from "./comprovantesSindicatoTypes";
import "./ComprovantesSindicatoTable.css";

export interface ComprovantesSindicatoTableProps {
  currentItems: ComprovantesSindicatoRow[];
  handleSort: (key: string) => void;
  getSortIcon: (key: string) => string;
  selectedYear?: number;
  onOpenHistory: (item: ComprovantesSindicatoRow) => void;
  formatDate?: (value: string | null | undefined) => string;
}

const TOOLTIP_ACEITE_EXTRATO =
  "O envio de comprovantes está indisponível. Favor dar aceite no extrato.";

const getSituationConfig = (
  situacaoRaw: string | undefined,
): { className: string; icon: React.ReactNode } => {
  const s = normalizeText(situacaoRaw);

  if (s.includes("comprovantes pendente")) {
    return { className: "status-pendente-sindicato", icon: <HourglassSplit /> };
  }

  if (s.includes("comprovantes enviado")) {
    return { className: "status-enviado", icon: <SendFill /> };
  }

  if (s.includes("correcao pendente")) {
    return { className: "status-adequacao-recusa", icon: <HourglassSplit /> };
  }

  if (s.includes("assinatura pendente")) {
    return { className: "status-adequacao-recusa", icon: <PenFill /> };
  }

  if (s.includes("assinatura expirada")) {
    return { className: "status-inapto", icon: <CalendarX /> };
  }

  if (s.includes("assinatura concluida")) {
    return { className: "status-plain", icon: <CheckCircleFill color="#067647" /> };
  }

  if (s.includes("inapto")) {
    return { className: "status-plain", icon: <XCircleFill color="#BA0C2F" /> };
  }

  return { className: "status-default", icon: <ExclamationCircle /> };
};

const ComprovantesSindicatoTable = ({
  currentItems,
  handleSort,
  getSortIcon,
  selectedYear,
  onOpenHistory,
  formatDate = formatDateTimeBR,
}: ComprovantesSindicatoTableProps) => {
  const navigate = useNavigate();

  const handleEdit = (item: ComprovantesSindicatoRow) => {
    if (!item.envioHabilitado) return;

    navigate(PATHS.EXTRACT_ENVIO_COMP, {
      state: {
        selectedYear: selectedYear ?? item.ano,
        extratoId: item.extratoId ?? item.solicitacaoId,
        solicitacaoId: item.extratoId ?? item.solicitacaoId,
        idSindicato: item.idSindicato,
        solicitacao: item.solicitacao,
        situacao: item.situacao,
      },
    });
  };

  return (
    <div className="table-responsive-wrapper">
      <Table
        bordered
        hover
        className="payment-table comprovantes-sindicato-table"
      >
        <colgroup>
          <col className="col-solicitacao-label" />
          <col className="col-situacao" />
          <col className="col-envio" />
          <col className="col-solicitacao" />
          <col className="col-correcao" />
          <col className="col-envio-formulario" />
          <col className="col-acoes" />
        </colgroup>

        <thead>
          <tr>
            <th
              onClick={() => handleSort("solicitacao")}
              className="sortable-header"
            >
              Solicitação {getSortIcon("solicitacao")}
            </th>
            <th
              onClick={() => handleSort("situacao")}
              className="sortable-header"
            >
              Situação {getSortIcon("situacao")}
            </th>
            <th onClick={() => handleSort("envio")} className="sortable-header">
              Envio dos Comprovantes {getSortIcon("envio")}
            </th>
            <th
              onClick={() => handleSort("solicitacaoCorrecao")}
              className="sortable-header"
            >
              Solicitação Correção {getSortIcon("solicitacaoCorrecao")}
            </th>
            <th
              onClick={() => handleSort("correcao")}
              className="sortable-header"
            >
              Correção dos Comprovantes {getSortIcon("correcao")}
            </th>
            <th
              onClick={() => handleSort("envioFormulario")}
              className="sortable-header"
            >
              Envio do Formulário {getSortIcon("envioFormulario")}
            </th>
            <th className="text-center">Ações</th>
          </tr>
        </thead>

        <tbody>
          {currentItems.length === 0 && (
            <tr>
              <td colSpan={7} className="empty-row">
                Nenhum comprovante encontrado.
              </td>
            </tr>
          )}

          {currentItems.map((item) => {
            const { icon, className } = getSituationConfig(item.situacao);

            const editDisabled = !item.envioHabilitado;
            const editTooltip = editDisabled
              ? TOOLTIP_ACEITE_EXTRATO
              : "Enviar / editar comprovantes";

            return (
              <tr key={item.rowKey}>
                <td>{item.solicitacao || "—"}</td>
                <td>
                  <div className="situation-cell">
                    <span className={`situation-icon ${className}`}>{icon}</span>
                    <span className="situation-text">
                      {item.situacao || "—"}
                    </span>
                  </div>
                </td>
                <td>{formatDate(item.envio)}</td>
                <td>{formatDate(item.solicitacaoCorrecao)}</td>
                <td>{formatDate(item.correcao)}</td>
                <td>{formatDate(item.envioFormulario)}</td>
                <td>
                  <div className="action-buttons d-flex gap-2 justify-content-center">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{editTooltip}</Tooltip>}
                    >
                      <span>
                        <Button
                          className="btn-edit"
                          size="sm"
                          onClick={() => handleEdit(item)}
                          disabled={editDisabled}
                          style={
                            editDisabled
                              ? { opacity: 0.35, cursor: "not-allowed" }
                              : undefined
                          }
                        >
                          <FaEdit />
                        </Button>
                      </span>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Ver histórico</Tooltip>}
                    >
                      <span>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onOpenHistory(item)}
                          disabled={!item.historicoHabilitado}
                        >
                          <FaClock />
                        </Button>
                      </span>
                    </OverlayTrigger>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default ComprovantesSindicatoTable;
